import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Brain, Zap, Target, Layers, Trophy, TrendingUp, Award, LogOut, User, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import Footer from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Dashboard() {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/profile`, {
        credentials: 'include'
      });
      if (!response.ok) throw new Error('Failed to fetch profile');
      const data = await response.json();
      setProfile(data);
    } catch (error) {
      toast.error('Помилка завантаження профілю');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
      navigate('/');
    } catch (error) {
      toast.error('Помилка виходу');
    }
  };

  const gameModes = [
    {
      id: 'quiz',
      title: 'Швидкий Квіз',
      description: 'Швидкі питання по архітектурним принципам',
      icon: Zap,
      color: 'from-blue-500 to-cyan-500',
      path: '/quiz'
    },
    {
      id: 'pvp',
      title: 'Architecture Battle (PvP)',
      description: 'Змагайся з іншими архітекторами в реальному часі',
      icon: Layers,
      color: 'from-orange-500 to-red-500',
      path: '/pvp',
      badge: 'NEW'
    },
    {
      id: 'scenario',
      title: 'Архітектурні Сценарії',
      description: 'Вирішуй реальні архітектурні проблеми',
      icon: Target,
      color: 'from-purple-500 to-pink-500',
      path: '/scenario'
    },
    {
      id: 'learning',
      title: 'Режим Навчання',
      description: 'Вивчай архітектурні концепції з найкращих книг',
      icon: BookOpen,
      color: 'from-teal-500 to-cyan-500',
      path: '/learning',
      badge: 'NEW'
    },
    {
      id: 'tradeoff',
      title: 'Trade-off Виклик',
      description: 'Балансуй між складністю, продуктивністю та вартістю',
      icon: Brain,
      color: 'from-green-500 to-emerald-500',
      path: '/tradeoff'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const levelProgress = ((profile?.xp % 100) / 100) * 100;

  return (
    <div className="min-h-screen bg-background">
      <div className="grid-background min-h-screen">
        <nav className="border-b border-border/50 backdrop-blur-sm bg-background/80">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                  <Brain className="w-6 h-6 text-white" />
                </div>
                <span className="text-xl font-bold text-foreground">Архітектор Майбутнього</span>
              </div>
              <div className="flex items-center space-x-4">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/profile')}
                  data-testid="profile-button"
                  className="flex items-center space-x-2"
                >
                  <User className="w-5 h-5" />
                  <span>{profile?.name}</span>
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleLogout}
                  data-testid="logout-button"
                  className="text-muted-foreground hover:text-foreground"
                >
                  <LogOut className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>
        </nav>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <h1 className="text-4xl md:text-5xl font-black tracking-tight text-foreground mb-2">
              Вітаємо, <span className="text-gradient">{profile?.name}</span>
            </h1>
            <p className="text-lg text-muted-foreground">Оберіть режим гри та розвивайте свої навички</p>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="lg:col-span-2 bg-card/50 backdrop-blur-sm border border-white/5 p-6 rounded-xl"
            >
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-2xl font-bold text-foreground">Рівень {profile?.level}</h3>
                  <p className="text-sm text-muted-foreground">{profile?.xp} XP</p>
                </div>
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center">
                  <Trophy className="w-10 h-10 text-white" />
                </div>
              </div>
              <Progress value={levelProgress} className="h-3" />
              <p className="text-xs text-muted-foreground mt-2">{100 - (profile?.xp % 100)} XP до наступного рівня</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-card/50 backdrop-blur-sm border border-white/5 p-6 rounded-xl"
            >
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Ігор зіграно</span>
                  <span className="text-2xl font-bold text-foreground">{profile?.games_played}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Точність</span>
                  <span className="text-2xl font-bold text-accent">{profile?.accuracy}%</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Досягнення</span>
                  <span className="text-2xl font-bold text-secondary">{profile?.achievements_count}</span>
                </div>
              </div>
            </motion.div>
          </div>

          <div className="mb-8">
            <h2 className="text-2xl font-bold text-foreground mb-4">Режими Гри</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {gameModes.map((mode, idx) => (
                <motion.div
                  key={mode.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 * (idx + 3) }}
                  onClick={() => navigate(mode.path)}
                  data-testid={`game-mode-${mode.id}`}
                  className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 p-8 hover:border-primary/50 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
                >
                  <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${mode.color} opacity-20 blur-3xl group-hover:opacity-30 transition-opacity`}></div>
                  <div className="relative z-10">
                    <div className="mb-4 w-14 h-14 rounded-xl bg-primary/20 flex items-center justify-center group-hover:bg-primary/30 transition-colors">
                      <mode.icon className="w-7 h-7 text-primary" />
                    </div>
                    {mode.badge && (
                      <span className="absolute top-0 right-0 px-2 py-1 text-xs font-bold bg-orange-500 text-white rounded-full">
                        {mode.badge}
                      </span>
                    )}
                    <h3 className="text-xl font-bold text-foreground mb-2">{mode.title}</h3>
                    <p className="text-muted-foreground mb-4">{mode.description}</p>
                    <div className="flex items-center text-primary font-medium">
                      <span>Грати зараз</span>
                      <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              onClick={() => navigate('/leaderboard')}
              data-testid="leaderboard-card"
              className="bg-card/50 backdrop-blur-sm border border-white/5 p-6 rounded-xl hover:border-primary/30 transition-all cursor-pointer"
            >
              <Trophy className="w-10 h-10 text-primary mb-4" />
              <h3 className="text-xl font-bold text-foreground mb-2">Таблиця Лідерів</h3>
              <p className="text-muted-foreground">Подивись на кращих архітекторів</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              onClick={() => navigate('/analytics')}
              data-testid="analytics-card"
              className="bg-card/50 backdrop-blur-sm border border-white/5 p-6 rounded-xl hover:border-primary/30 transition-all cursor-pointer"
            >
              <TrendingUp className="w-10 h-10 text-accent mb-4" />
              <h3 className="text-xl font-bold text-foreground mb-2">Аналітика</h3>
              <p className="text-muted-foreground">Відстежуй свій прогрес</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              onClick={() => navigate('/achievements')}
              data-testid="achievements-card"
              className="bg-card/50 backdrop-blur-sm border border-white/5 p-6 rounded-xl hover:border-primary/30 transition-all cursor-pointer"
            >
              <Award className="w-10 h-10 text-secondary mb-4" />
              <h3 className="text-xl font-bold text-foreground mb-2">Досягнення</h3>
              <p className="text-muted-foreground">Розблокуй значки майстерності</p>
            </motion.div>
          </div>
        </div>
        <Footer />
      </div>
    </div>
  );
}